import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Spinner, Tab, Tabs, Table, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import ExternalNavbar from '../components/ExternalNavbar';
import ProgressReportGenerator from '../components/ProgressReportGenerator';
import '../styles/brand.css';

const ExternalDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalLearners: 0,
    activeLearners: 0,
    totalCohorts: 0,
    totalModules: 0
  });
  const [cohorts, setCohorts] = useState([]);
  const [learners, setLearners] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user has external role
    if (!userProfile || userProfile.role !== 'external') {
      navigate('/');
      return;
    }

    loadDashboardData();
  }, [currentUser, userProfile, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load cohorts
      const cohortsSnapshot = await getDocs(collection(db, 'cohorts'));
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCohorts(cohortsData);

      // Load users (learners)
      const usersSnapshot = await getDocs(query(
        collection(db, 'users'),
        where('role', '==', 'learner')
      ));
      const learnersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLearners(learnersData);

      // Load modules
      const modulesSnapshot = await getDocs(collection(db, 'modules'));

      // Calculate stats
      const activeLearners = learnersData.filter(l => l.isActive || l.status === 'active').length;

      setStats({
        totalLearners: learnersData.length,
        activeLearners: activeLearners,
        totalCohorts: cohortsData.length,
        totalModules: modulesSnapshot.size
      });

    } catch (err) {
      devError('Error loading dashboard data:', err);
      setError('Failed to load dashboard data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCompletionRate = (learner) => {
    if (!learner.documents) return 0;
    const docs = Object.values(learner.documents);
    const verified = docs.filter(d => d.status === 'verified').length;
    const total = docs.length;
    return total > 0 ? Math.round((verified / total) * 100) : 0;
  };

  return (
    <>
      {/* External Navbar */}
      <ExternalNavbar />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">External Client Dashboard</h1>
              <p className="mission-text mb-4">
                Access comprehensive learner progress reports and analytics
              </p>
              <p className="mb-0">
                Generate detailed progress reports, view learner statistics, and track completion rates
                for your assigned programs.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="light" onClick={loadDashboardData}>
                Refresh
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading dashboard data...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Error Alert */}
            {error && (
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            )}

            {/* Quick Stats */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="text-center shadow-sm">
                  <Card.Body>
                    <h2 className="mb-0 text-primary">{stats.totalLearners}</h2>
                    <p className="text-muted mb-0">Total Learners</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center shadow-sm">
                  <Card.Body>
                    <h2 className="mb-0 text-success">{stats.activeLearners}</h2>
                    <p className="text-muted mb-0">Active Learners</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center shadow-sm">
                  <Card.Body>
                    <h2 className="mb-0 text-info">{stats.totalCohorts}</h2>
                    <p className="text-muted mb-0">Cohorts</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center shadow-sm">
                  <Card.Body>
                    <h2 className="mb-0 text-warning">{stats.totalModules}</h2>
                    <p className="text-muted mb-0">Modules</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Tabs */}
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
              {/* Overview Tab */}
              <Tab eventKey="overview" title="Overview">
                <Card>
                  <Card.Header>
                    <h5>System Overview</h5>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info">
                      <strong>External Client Dashboard</strong>
                      <p className="mb-0 mt-2">
                        As an external client, you have read-only access to learner data and can generate
                        progress reports. Use the tabs above to explore different sections
                        or click "Generate Reports" to create detailed progress reports.
                      </p>
                    </Alert>

                    <Row>
                      <Col md={6}>
                        <h6 className="mb-3">Your Permissions</h6>
                        <ul>
                          <li>Can view learner profiles and progress</li>
                          <li>Can view cohorts and modules</li>
                          <li>Can view attendance records</li>
                          <li>Can generate progress reports</li>
                          <li>Can export reports to PDF/Excel</li>
                          <li>Cannot modify learner data</li>
                          <li>Cannot modify system settings</li>
                        </ul>
                      </Col>
                      <Col md={6}>
                        <h6 className="mb-3">Quick Actions</h6>
                        <div className="d-grid gap-2">
                          <Button variant="primary" onClick={() => setActiveTab('reports')}>
                            Generate Progress Report
                          </Button>
                          <Button variant="outline-primary" onClick={() => setActiveTab('learners')}>
                            View Learners
                          </Button>
                          <Button variant="outline-primary" onClick={() => setActiveTab('cohorts')}>
                            View Cohorts
                          </Button>
                          <Button variant="outline-secondary" onClick={() => navigate('/resources')}>
                            Browse Resources
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Tab>

              {/* Learners Tab */}
              <Tab eventKey="learners" title={`Learners (${learners.length})`}>
                <Card>
                  <Card.Header>
                    <h5>Learner Directory</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Cohort</th>
                          <th>Status</th>
                          <th>Document Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {learners.slice(0, 50).map(learner => (
                          <tr key={learner.id}>
                            <td>{learner.displayName || 'N/A'}</td>
                            <td>{learner.email}</td>
                            <td>
                              {learner.cohortId
                                ? cohorts.find(c => c.id === learner.cohortId)?.name || learner.cohortId
                                : 'Not assigned'}
                            </td>
                            <td>
                              <Badge bg={learner.isActive ? 'success' : 'secondary'}>
                                {learner.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="progress flex-grow-1 me-2" style={{ height: '20px' }}>
                                  <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${getCompletionRate(learner)}%` }}
                                    aria-valuenow={getCompletionRate(learner)}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  >
                                    {getCompletionRate(learner)}%
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {learners.length > 50 && (
                      <Alert variant="info" className="mt-3">
                        Showing first 50 learners. Use the Progress Reports tab to generate detailed reports
                        with filters.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Cohorts Tab */}
              <Tab eventKey="cohorts" title={`Cohorts (${cohorts.length})`}>
                <Card>
                  <Card.Header>
                    <h5>Cohorts Overview</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {cohorts.map(cohort => {
                        const cohortLearners = learners.filter(l => l.cohortId === cohort.id);
                        return (
                          <Col md={6} lg={4} key={cohort.id} className="mb-3">
                            <Card className="h-100">
                              <Card.Body>
                                <h6>{cohort.name}</h6>
                                <p className="text-muted small mb-2">{cohort.description || 'No description'}</p>
                                <hr />
                                <div className="d-flex justify-content-between">
                                  <small className="text-muted">Learners:</small>
                                  <strong>{cohortLearners.length}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <small className="text-muted">Active:</small>
                                  <strong className="text-success">
                                    {cohortLearners.filter(l => l.isActive).length}
                                  </strong>
                                </div>
                                {cohort.startDate && (
                                  <div className="d-flex justify-content-between">
                                    <small className="text-muted">Start Date:</small>
                                    <small>{new Date(cohort.startDate).toLocaleDateString()}</small>
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                    {cohorts.length === 0 && (
                      <Alert variant="info">No cohorts found.</Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* Progress Reports Tab */}
              <Tab eventKey="reports" title="Progress Reports">
                <Card>
                  <Card.Header>
                    <h5>Generate Progress Reports</h5>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info" className="mb-4">
                      <strong>External Client Reports:</strong> Generate comprehensive reports on learner
                      progress, completion rates, and document verification status. Select filters below
                      to customize your report.
                    </Alert>
                    <ProgressReportGenerator />
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </>
        )}
      </Container>
    </>
  );
};

export default ExternalDashboard;
